import React from 'react';
import PropTypes from 'prop-types';
import Carousel from '@ow/ow-components/lib/components/Carousel';
import theme from '@ow/ow-components/lib/theme';
import styled from 'styled-components';
import Icon from '@ow/ow-components/lib/components/Icon';
import { Picture } from 'pixboost-react';
import { apiKey, domain, breakpoints } from '../../constants/imageOptimisation';
import Jumbotron from './jumbotron';
import BaseFadeCarousel from './FadeCarousel';

const Wrapper = styled.div`
  height: 100%;
  > div {
    height: 100%;
  }
`;

const FadeCarousel = styled(BaseFadeCarousel)`
  & a.carousel-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    height: 100%;
    flex-direction: column;
  }

  & a.carousel-slide > .styled-picture {
    /* needs to have min size, otherwise IE Edge is not firing intersection event */
    min-height: 1px;
    min-width: 1px;
    height: 100%;
    @media ${theme.mediaSize.largeMin} {
      display: flex;
    }

    source,
    img {
      height: auto;
      max-width: 100%;

      @media ${theme.mediaSize.largeMin} {
        height: 100%;
        max-width: none;
      }
    }
  }
`;

const ButtonBase = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  padding: ${theme.space.half};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.elevated};
  opacity: 0.5;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const LeftButtonContainer = styled(ButtonBase)`
  left: 0;
`;
const RightButtonContainer = styled(ButtonBase)`
  right: 0;
`;

const makeButton = (ContainerComponent, iconName, onClick) => (
  <ContainerComponent onClick={onClick}>
    <Icon
      name={iconName}
      width={theme.space.double}
      height={theme.space.double}
      color={theme.colors.silver}
    />
  </ContainerComponent>
);

const renderLeftButton = ({ onClick }) =>
  makeButton(LeftButtonContainer, 'components--slider--chevron', onClick);
const renderRightButton = ({ onClick }) =>
  makeButton(RightButtonContainer, 'components--slider--chevron-inverted', onClick);

const ContentEntryCarousel = props => {
  const renderSlides = () =>
    props.content.items.map((carouselItem, index) => (
      <a
        className="carousel-slide"
        key={`${index}-${carouselItem.imageUrl}`}
        href={carouselItem.link}
        target={carouselItem.target}
        title={carouselItem.title}
      >
        <Picture
          className="styled-picture"
          alt={carouselItem.title}
          breakpoints={{
            lg: {
              src: carouselItem.imageUrl
            },
            md: {
              src: carouselItem.imageUrl
            },
            sm: {
              src: carouselItem.imageMobileUrl || carouselItem.imageUrl
            }
          }}
          config={{ domain, apiKey, breakpoints }}
        />
      </a>
    ));

  if (props.content.variation === 'jumbotron') {
    return (
      <Jumbotron
        items={props.content.items}
        renderLeftButton={renderLeftButton}
        renderRightButton={renderRightButton}
      />
    );
  } else if (props.content.variation === 'fade-gallery') {
    return (
      <FadeCarousel renderLeftButton={renderLeftButton} renderRightButton={renderRightButton}>
        {renderSlides()}
      </FadeCarousel>
    );
  }

  return (
    <Wrapper>
      <Carousel
        itemsPerSlide={1}
        autoplay={true}
        interval={5000}
        showPagination={false}
        leftButton={renderLeftButton}
        rightButton={renderRightButton}
        disableTileStyles={true}
      >
        {renderSlides()}
      </Carousel>
    </Wrapper>
  );
};

ContentEntryCarousel.propTypes = {
  content: PropTypes.object
};

export default ContentEntryCarousel;
